import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import * as Sentry from '@sentry/react';

import { App } from '@/app/App';
import { queryClient } from '@/lib/queryClient';
import { trpc, trpcClient } from '@/lib/trpc';
import { initSentry } from '@/lib/sentry';
import { registerServiceWorker } from '@/lib/serviceWorker';
// Side-effect import: initialises i18next before the first render, so no
// component ever mounts against an uninitialised translation instance.
import '@/lib/i18n';
import '@/styles/index.css';

initSentry();
registerServiceWorker();

// Phase 2 (audit sweep 2026-05-12): chunk-load auto-recovery.
// Vite emits `vite:preloadError` when a `React.lazy()` import fails
// — usually because a stale index.html references chunk hashes that
// the new deploy already pruned, or the CDN edge is still
// propagating mid-deploy. The audit caught this on `/admin/audit`
// and `/tech` first-nav-after-deploy: user sees the
// "Something went sideways" boundary and has to manually reload.
// One-shot recovery does it for them.
//
// Guard against reload loops via sessionStorage: if we've already
// auto-reloaded for this session, surface the second failure
// normally so the user knows something else is wrong.
window.addEventListener('vite:preloadError', (event) => {
  const RELOAD_FLAG = 'fw-chunk-reload-once';
  try {
    if (sessionStorage.getItem(RELOAD_FLAG)) return;
    sessionStorage.setItem(RELOAD_FLAG, '1');
  } catch {
    // sessionStorage unavailable (private mode quotas, etc.) — fall
    // through to reload anyway; worst case is one reload per failure.
  }
  event.preventDefault();
  window.location.reload();
});

const rootEl = document.getElementById('root');
if (!rootEl) {
  throw new Error('Root element #root not found in index.html.');
}

ReactDOM.createRoot(rootEl).render(
  <React.StrictMode>
    <Sentry.ErrorBoundary fallback={<GlobalErrorFallback />}>
      <trpc.Provider client={trpcClient} queryClient={queryClient}>
        <QueryClientProvider client={queryClient}>
          <BrowserRouter>
            <App />
          </BrowserRouter>
        </QueryClientProvider>
      </trpc.Provider>
    </Sentry.ErrorBoundary>
  </React.StrictMode>,
);

function GlobalErrorFallback() {
  return (
    <main className="fw-hero-bg flex min-h-screen items-center justify-center p-6">
      <div className="fw-card max-w-lg text-center">
        <h1 className="mb-2 text-2xl">Something went sideways.</h1>
        <p className="text-fw-text-secondary mb-6">
          Roldi lost its wrench. Reload the page — if it keeps happening, tell Dave.
        </p>
        <button
          type="button"
          className="fw-button-primary"
          onClick={() => window.location.reload()}
        >
          Reload
        </button>
      </div>
    </main>
  );
}
